// src/admin/components/ReturnsManagement.tsx
import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  Tooltip,
  Pagination,
  TextField,
  InputAdornment,
  Collapse,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Undo as UndoIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  PurchaseOrderReturn,
  ReturnStatus,
  RETURN_STATUS_COLORS,
} from './PurchaseOrder';
import { PurchaseOrderReturnService } from './purchaseOrderReturnService';
import { useAuth } from '../../../contexts/AuthContext';
import ReturnViewDialog from './ReturnViewDialog';
import ToastNotification from '../common/Toastnotification';
import { useCompany } from '../../../contexts/CompanyContext';
import { getCurrencySymbol } from '../../../util/productUtils';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import { formatCurrency } from '../../../util/Formatcurrency';
import { useReturnsPaginated, useReturnStats, useReturnMutations, RETURN_KEYS } from '../../../hooks/useReturns';
import { useQueryClient } from '@tanstack/react-query';

const RETURNS_PER_PAGE = 10;

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const getSoftChipSx = (color: string, isDark: boolean = false) => {
  type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

  const lightPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
    info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
    primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
    success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
    error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
    secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
    default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
  };

  const darkPalette: Record<string, PaletteEntry> = {
    warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
    info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
    primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
    success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
    error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
    secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
    default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
  };

  const palette = isDark ? darkPalette : lightPalette;
  const p = palette[color] ?? palette.default;

  return {
    backgroundColor: p.bg,
    borderColor: p.border,
    color: p.text,
    fontWeight: 600,
    border: `1px solid ${p.border}`,
    '& .MuiChip-icon': { color: p.iconColor },
    '& .MuiChip-label': { color: p.text },
  };
};

const returnStatusToSoftColor: Record<string, string> = {
  success: 'success', warning: 'warning', info: 'info', error: 'error',
  primary: 'primary', secondary: 'secondary', default: 'default',
};

interface LoadingOverlayProps {
  wrapperRef: React.RefObject<HTMLDivElement | null>;
}

const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const theme = useTheme();
  const overlayBg = theme.palette.mode === 'dark'
    ? 'rgba(26, 29, 36, 0.85)'
    : 'rgba(255, 255, 255, 0.85)';

  React.useEffect(() => {
    const measure = () => {
      if (wrapperRef.current) setRect(wrapperRef.current.getBoundingClientRect());
    };
    measure();
    const el = wrapperRef.current;
    window.addEventListener('resize', measure);
    el?.addEventListener('scroll', measure);
    return () => {
      window.removeEventListener('resize', measure);
      el?.removeEventListener('scroll', measure);
    };
  }, [wrapperRef]);

  if (!rect) return null;

  return (
    <Box sx={{
      position: 'fixed', top: rect.top, left: rect.left,
      width: rect.width, height: rect.height,
      backgroundColor: overlayBg, backdropFilter: 'blur(2px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1100, pointerEvents: 'all', borderRadius: 'inherit',
    }}>
      <Box sx={{ textAlign: 'center' }}>
        <CircularProgress size={40} />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading returns...</Typography>
      </Box>
    </Box>
  );
};

const ReturnsManagement: React.FC = () => {
  const { currentUser } = useAuth();
  const queryClient = useQueryClient();
  const invalidateAll = () => queryClient.invalidateQueries({ queryKey: RETURN_KEYS.all });

  // ── UI state ──────────────────────────────────────────────────────────────
  const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState<PurchaseOrderReturn | null>(null);
  const [deleteLoading, setDeleteLoading] = useState<string>('');
  const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });
  const [deleteTarget, setDeleteTarget] = useState<PurchaseOrderReturn | null>(null);
  const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);

  // ── Pagination & filter state ─────────────────────────────────────────────
  // FIX: Removed all cursor state (lastVisible, firstVisible, cursors).
  // Seek-based pagination only needs the current page number.
  const [currentPage, setCurrentPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);

  const tableWrapperRef = useRef<HTMLDivElement>(null);

  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { companyProfile } = useCompany();
  const companyCurrency = companyProfile?.currency || 'LKR';
  const currencySymbol = getCurrencySymbol(companyCurrency);

  // ── React Query hooks ─────────────────────────────────────────────────────
  // FIX: No longer passes a `cursors` object — just page number + filters.
  const {
    data: returnsData,
    isLoading: returnsLoading,
    isFetching: returnsIsFetching,
    isError: returnsError,
    error: returnsErrorObj,
  } = useReturnsPaginated({
    page: currentPage,
    search: searchQuery,
    status: statusFilter,
    pageSize: RETURNS_PER_PAGE,
  });

  const { data: statsData } = useReturnStats();
  const { remove } = useReturnMutations();

  // ── Derived values ────────────────────────────────────────────────────────
  const returns = returnsData?.returns ?? [];
  const totalReturns = returnsData?.totalCount ?? 0;
  const totalPages = Math.ceil(totalReturns / RETURNS_PER_PAGE);
  const loading = returnsLoading || returnsIsFetching;

  const totalValue = statsData?.totalValue ?? 0;
  const totalRefunded = statsData?.totalRefunded ?? 0;
  const totalOutstanding = totalValue - totalRefunded;

  // ── Helpers ───────────────────────────────────────────────────────────────
  const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setToast({ open: true, message, severity });
  };
  const handleCloseToast = () => setToast(t => ({ ...t, open: false }));

  // ── Reset to page 1 ───────────────────────────────────────────────────────
  // FIX: Simplified — no cursor state to reset, just set page to 1.
  const resetToFirstPage = () => {
    setCurrentPage(1);
  };

  // ── Pagination handler ────────────────────────────────────────────────────
  // FIX: Simplified — just set the page number directly.
  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  // ── Search ────────────────────────────────────────────────────────────────
  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setSearchQuery(searchInput);
      resetToFirstPage();
    }
  };

  // ── Filters ───────────────────────────────────────────────────────────────
  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value);
    resetToFirstPage();
  };

  const clearFilters = () => {
    setSearchInput('');
    setSearchQuery('');
    setStatusFilter('all');
    resetToFirstPage();
    showToast('Filters cleared', 'info');
  };

  const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
  const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteReturn = (returnDoc: PurchaseOrderReturn) => setDeleteTarget(returnDoc);

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    try {
      setDeleteDialogLoading(true);
      setDeleteLoading(deleteTarget.id!);
      await remove.mutateAsync(deleteTarget.id!);
      setDeleteTarget(null);
      resetToFirstPage();
      showToast('Return deleted successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to delete return: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setDeleteDialogLoading(false);
      setDeleteLoading('');
    }
  };

  const handleDeleteCancel = () => setDeleteTarget(null);

  // ── View ──────────────────────────────────────────────────────────────────
  const handleViewReturn = (returnDoc: PurchaseOrderReturn) => {
    setSelectedReturn(returnDoc);
    setViewDrawerOpen(true);
  };

  // ── Status update (called from ReturnViewDialog) ──────────────────────────
  // ReturnViewDialog calls service methods directly, so we must invalidate
  // the cache here — same pattern as PurchaseOrdersManagement.handleStatusUpdate.
  const handleStatusUpdate = async (returnId: string, status: ReturnStatus) => {
    if (!currentUser) { showToast('You must be logged in to update status', 'error'); return; }
    try {
      await PurchaseOrderReturnService.updateReturnStatus(
        returnId, status,
        currentUser.uid,
        currentUser.displayName || currentUser.email || 'Unknown'
      );
      invalidateAll();
      resetToFirstPage();
      showToast('Return status updated successfully', 'success');
    } catch (err: any) {
      showToast(`Failed to update status: ${err.message || 'Unknown error'}`, 'error');
      throw err;
    }
  };

  // ── Refresh (called from ReturnViewDialog.onRefresh after create/complete) ─
  // ReturnViewDialog's onRefresh is invoked after POReturnDialog creates a new
  // return or after completeReturnWithInventoryAdjustment — both bypass React
  // Query mutations, so invalidateAll() is essential here.
  const handleRefresh = () => {
    invalidateAll();
    resetToFirstPage();
    showToast('Returns refreshed', 'info');
  };

  const getStatusIcon = (status: ReturnStatus) => {
    switch (status) {
      case ReturnStatus.APPROVED:
      case ReturnStatus.COMPLETED: return <CheckCircleIcon fontSize="small" />;
      case ReturnStatus.REJECTED: return <CancelIcon fontSize="small" />;
      default: return null;
    }
  };

  if (loading && returns.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
        <CircularProgress size={40} />
        <Typography variant="body1" sx={{ ml: 2 }}>Loading returns...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{
      height: { md: '100%' },
      display: 'flex', flexDirection: 'column',
      overflow: { xs: 'visible', md: 'hidden' },
      p: { xs: 2, sm: 3 },
    }}>
      <Box sx={{ flexShrink: 0 }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2} sx={{ mb: 3 }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <AssignmentReturnIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
                Purchase Order Returns
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage damaged and returned items
              </Typography>
            </Box>
          </Stack>
        </Stack>

        {/* {returnsError && (
          <Alert severity="error" sx={{ mb: 3 }}
            action={
              <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
                Retry
              </Button>
            }
          >
            {(returnsErrorObj as Error)?.message || 'Failed to fetch returns'}
          </Alert>
        )} */}

        {/* ── Stats Cards ── */}
        <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">
          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Returns</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}>
                  {totalReturns}
                </Typography>
                <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="success.main" noWrap>
                      {returns.filter(r => r.status === ReturnStatus.COMPLETED).length} completed
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
                    <Typography variant="caption" color="warning.main" noWrap>
                      {returns.filter(r => r.status === ReturnStatus.PENDING).length} pending
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Value</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalValue, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>across all returns</Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Total Refunded</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalRefunded, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalValue > 0 ? `${((totalRefunded / totalValue) * 100).toFixed(1)}% of total` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
            <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
                <Typography color="text.secondary" gutterBottom variant="body2" noWrap>Pending Refund</Typography>
                <Typography variant="h5" component="div" color="text.primary"
                  sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}>
                  {formatCurrency(totalOutstanding, currencySymbol)}
                </Typography>
                <Box sx={{ mt: 'auto' }}>
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {totalValue > 0 ? `${((totalOutstanding / totalValue) * 100).toFixed(1)}% unrefunded` : '—'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* ── Search & Filters ── */}
        <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between">
            <TextField
              placeholder="Search by return number, PO... (Press Enter)"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={handleSearchKeyPress}
              sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
              size="small"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
            />
            <Stack direction="row" alignItems="center" spacing={1}>
              <Button
                onClick={() => setShowFilters(!showFilters)}
                variant={hasActiveFilters ? 'contained' : 'outlined'}
                startIcon={<FilterListIcon />}
                endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                size="small"
              >
                Filters {hasActiveFilters && `(${activeFiltersCount})`}
              </Button>
              {hasActiveFilters && (
                <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
                  Clear all
                </Button>
              )}
            </Stack>
          </Stack>

          <Collapse in={showFilters}>
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Return Status</InputLabel>
                    <Select value={statusFilter} onChange={(e) => handleStatusFilterChange(e.target.value)} label="Return Status">
                      <MenuItem value="all">All Status</MenuItem>
                      <MenuItem value={ReturnStatus.PENDING}>Pending</MenuItem>
                      <MenuItem value={ReturnStatus.APPROVED}>Approved</MenuItem>
                      <MenuItem value={ReturnStatus.REJECTED}>Rejected</MenuItem>
                      <MenuItem value={ReturnStatus.COMPLETED}>Completed</MenuItem>
                      <MenuItem value={ReturnStatus.CANCELLED}>Cancelled</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={9}>
                  <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
                    <Typography variant="body2" color="text.secondary">
                      {hasActiveFilters
                        ? `Found ${returns.length} returns matching your filters`
                        : `Showing ${returns.length} returns on this page`}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Collapse>
        </Paper>
      </Box>

      {/* ── Scrollable Body ── */}
      <Box sx={{ flex: 1, overflow: { xs: 'visible', md: 'auto' }, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
        {returns.length === 0 && !loading && (
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Card sx={{ width: '100%', maxWidth: 500 }}>
              <CardContent sx={{ textAlign: 'center', py: 6 }}>
                <UndoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h5" color="text.secondary" gutterBottom>
                  {hasActiveFilters ? 'No Returns Match Your Filters' : 'No Returns Found'}
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
                  {hasActiveFilters
                    ? 'Try adjusting your search or filter criteria.'
                    : 'No returns have been created yet.'}
                </Typography>
                {hasActiveFilters && (
                  <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>Clear Filters</Button>
                )}
              </CardContent>
            </Card>
          </Box>
        )}

        {returns.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>
            <TableContainer
              component={Paper}
              ref={tableWrapperRef}
              sx={{
                flex: 1, overflow: { xs: 'auto', md: 'auto' },
                maxHeight: { md: '100%' }, mb: 2, position: 'relative',
                '& .MuiTable-root': { minWidth: { xs: 600, md: 1000 } },
              }}
            >
              <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell>Return Number</TableCell>
                    <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>PO Number</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Supplier</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                    <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Refund</TableCell>
                    <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created By</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {returns.map((returnDoc) => (
                    <TableRow key={returnDoc.id} hover sx={{
                      '&:hover': { backgroundColor: 'action.hover' },
                      '&:last-child td, &:last-child th': { borderBottom: 0 },
                    }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {returnDoc.returnNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {returnDoc.createdAt.toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: { xs: 'block', sm: 'none' } }}>
                          {returnDoc.purchaseOrderNumber}
                        </Typography>
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: { xs: 'block', md: 'none' } }}>
                          {returnDoc.supplierName}
                        </Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        <Typography variant="body2">{returnDoc.purchaseOrderNumber}</Typography>
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{returnDoc.supplierName}</Typography>
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={returnDoc.status} size="small" variant="outlined"
                          icon={getStatusIcon(returnDoc.status) || undefined}
                          sx={getSoftChipSx(
                            returnStatusToSoftColor[RETURN_STATUS_COLORS[returnDoc.status]] ?? 'default',
                            isDark
                          )}
                        />
                      </TableCell>

                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                          {formatCurrency(returnDoc.totalAmount, currencySymbol)}
                        </Typography>
                      </TableCell>

                      <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                        {returnDoc.refundIssued ? (
                          <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
                            {formatCurrency(returnDoc.refundAmount, currencySymbol)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">-</Typography>
                        )}
                      </TableCell>

                      <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                        <Typography variant="body2">{returnDoc.createdByName}</Typography>
                      </TableCell>

                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
                          <Tooltip title="View Details">
                            <IconButton onClick={() => handleViewReturn(returnDoc)} size="small" color="primary">
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          {!returnDoc.inventoryAdjusted && (
                            <Tooltip title="Delete">
                              <IconButton
                                onClick={() => handleDeleteReturn(returnDoc)}
                                disabled={deleteLoading === returnDoc.id}
                                size="small" color="error"
                              >
                                {deleteLoading === returnDoc.id
                                  ? <CircularProgress size={16} />
                                  : <DeleteIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

            <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
              <Box sx={{ py: 2, px: 2 }}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={{ xs: 1.5, sm: 3 }}
                  alignItems="center" justifyContent="space-between" sx={{ width: '100%' }}
                >
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Showing {totalReturns === 0 ? 0 : (currentPage - 1) * RETURNS_PER_PAGE + 1}–
                    {Math.min(currentPage * RETURNS_PER_PAGE, totalReturns)} of {totalReturns} returns
                  </Typography>
                  <Pagination
                    count={Math.max(1, totalPages)}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    size={isMobile ? 'small' : 'medium'}
                    showFirstButton showLastButton
                    disabled={loading || totalPages <= 1}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    Page {currentPage} of {Math.max(1, totalPages)}
                  </Typography>
                </Stack>
              </Box>
            </Paper>
          </Box>
        )}
      </Box>

      {selectedReturn && (
        <ReturnViewDialog
          open={viewDrawerOpen}
          onClose={() => { setViewDrawerOpen(false); setSelectedReturn(null); }}
          return={selectedReturn}
          onStatusUpdate={handleStatusUpdate}
          onRefresh={handleRefresh}
        />
      )}

      <Dialog open={!!deleteTarget} onClose={handleDeleteCancel} maxWidth="xs" fullWidth>
        <DialogTitle>Delete Return</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete{' '}
            <strong>"{deleteTarget?.returnNumber}"</strong>?{' '}
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteDialogLoading}
            startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default ReturnsManagement;


// // src/admin/components/ReturnsManagement.tsx
// import React, { useState, useEffect, useRef } from 'react';
// import {
//   Box,
//   Button,
//   Card,
//   CardContent,
//   Grid,
//   IconButton,
//   Paper,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Typography,
//   Alert,
//   CircularProgress,
//   Stack,
//   Chip,
//   Tooltip,
//   Pagination,
//   TextField,
//   InputAdornment,
//   Collapse,
//   FormControl,
//   InputLabel,
//   Select,
//   MenuItem,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogContentText,
//   DialogTitle,
//   useTheme,
//   useMediaQuery,
// } from '@mui/material';
// import {
//   Delete as DeleteIcon,
//   Visibility as VisibilityIcon,
//   Undo as UndoIcon,
//   CheckCircle as CheckCircleIcon,
//   Cancel as CancelIcon,
//   Search as SearchIcon,
//   FilterList as FilterListIcon,
//   Clear as ClearIcon,
//   ExpandMore as ExpandMoreIcon,
//   ExpandLess as ExpandLessIcon,
//   Refresh as RefreshIcon,
// } from '@mui/icons-material';
// import {
//   PurchaseOrderReturn,
//   ReturnStatus,
//   RETURN_STATUS_COLORS,
// } from './PurchaseOrder';
// import { PurchaseOrderReturnService } from './purchaseOrderReturnService';
// import { useAuth } from '../../../contexts/AuthContext';
// import ReturnViewDialog from './ReturnViewDialog';
// import ToastNotification from '../common/Toastnotification';
// import { useCompany } from '../../../contexts/CompanyContext';
// import { getCurrencySymbol } from '../../../util/productUtils';
// import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
// import { formatCurrency } from '../../../util/Formatcurrency';

// const RETURNS_PER_PAGE = 10;

// interface ToastState {
//   open: boolean;
//   message: string;
//   severity: 'success' | 'error' | 'warning' | 'info';
// }

// // ── Theme-aware chip style helper ─────────────────────────────────────────────
// //
// // In light mode: soft pastel tints (low-opacity fills).
// // In dark mode:  richer semi-transparent fills + brighter text so the chip
// //                is legible against dark paper backgrounds.
// //
// // Pass `isDark` (from useTheme) to switch between the two palettes.
// // ─────────────────────────────────────────────────────────────────────────────
// const getSoftChipSx = (color: string, isDark: boolean = false) => {
//   type PaletteEntry = { bg: string; border: string; text: string; iconColor: string };

//   const lightPalette: Record<string, PaletteEntry> = {
//     warning: { bg: 'rgba(255,167,38,0.12)', border: 'rgba(255,167,38,0.4)', text: '#b76e00', iconColor: '#e68900' },
//     info: { bg: 'rgba(41,182,246,0.12)', border: 'rgba(41,182,246,0.4)', text: '#0277bd', iconColor: '#039be5' },
//     primary: { bg: 'rgba(33,150,243,0.12)', border: 'rgba(33,150,243,0.4)', text: '#1565c0', iconColor: '#1976d2' },
//     success: { bg: 'rgba(102,187,106,0.15)', border: 'rgba(102,187,106,0.45)', text: '#2e7d32', iconColor: '#43a047' },
//     error: { bg: 'rgba(239,83,80,0.12)', border: 'rgba(239,83,80,0.4)', text: '#c62828', iconColor: '#e53935' },
//     secondary: { bg: 'rgba(171,71,188,0.10)', border: 'rgba(171,71,188,0.35)', text: '#6a1b9a', iconColor: '#8e24aa' },
//     default: { bg: 'rgba(158,158,158,0.12)', border: 'rgba(158,158,158,0.4)', text: '#424242', iconColor: '#757575' },
//   };

//   // Dark mode: higher-opacity fills + light/vivid text so chips pop on dark surfaces
//   const darkPalette: Record<string, PaletteEntry> = {
//     warning: { bg: 'rgba(255,167,38,0.22)', border: 'rgba(255,167,38,0.55)', text: '#ffcc80', iconColor: '#ffa726' },
//     info: { bg: 'rgba(41,182,246,0.20)', border: 'rgba(41,182,246,0.50)', text: '#81d4fa', iconColor: '#29b6f6' },
//     primary: { bg: 'rgba(33,150,243,0.22)', border: 'rgba(33,150,243,0.50)', text: '#90caf9', iconColor: '#42a5f5' },
//     success: { bg: 'rgba(102,187,106,0.22)', border: 'rgba(102,187,106,0.50)', text: '#a5d6a7', iconColor: '#66bb6a' },
//     error: { bg: 'rgba(239,83,80,0.22)', border: 'rgba(239,83,80,0.50)', text: '#ef9a9a', iconColor: '#ef5350' },
//     secondary: { bg: 'rgba(171,71,188,0.22)', border: 'rgba(171,71,188,0.50)', text: '#ce93d8', iconColor: '#ab47bc' },
//     default: { bg: 'rgba(158,158,158,0.22)', border: 'rgba(158,158,158,0.50)', text: '#e0e0e0', iconColor: '#bdbdbd' },
//   };

//   const palette = isDark ? darkPalette : lightPalette;
//   const p = palette[color] ?? palette.default;

//   return {
//     backgroundColor: p.bg,
//     borderColor: p.border,
//     color: p.text,
//     fontWeight: 600,
//     border: `1px solid ${p.border}`,
//     '& .MuiChip-icon': { color: p.iconColor },
//     '& .MuiChip-label': { color: p.text },
//   };
// };

// // Map RETURN_STATUS_COLORS values to soft palette keys
// const returnStatusToSoftColor: Record<string, string> = {
//   success: 'success',
//   warning: 'warning',
//   info: 'info',
//   error: 'error',
//   primary: 'primary',
//   secondary: 'secondary',
//   default: 'default',
// };

// // ── LoadingOverlay ────────────────────────────────────────────────────────────
// // Renders a `position: fixed` backdrop that exactly covers the scroll container
// // referenced by `wrapperRef`, regardless of how far the user has scrolled.
// //
// // WHY: A `position: absolute` overlay *inside* a TableContainer (which has
// // overflow:auto) is positioned relative to the element's full scrollable
// // height, not its visible viewport. After scrolling down, the top-anchored
// // overlay moves off screen. Using `position: fixed` + getBoundingClientRect()
// // always covers exactly what is currently visible on screen.
// //
// // DARK MODE: backdrop colour is derived from the MUI theme so it matches the
// // current paper background instead of always being white.
// // ─────────────────────────────────────────────────────────────────────────────
// interface LoadingOverlayProps {
//   wrapperRef: React.RefObject<HTMLDivElement | null>;
// }

// const LoadingOverlay: React.FC<LoadingOverlayProps> = ({ wrapperRef }) => {
//   const [rect, setRect] = useState<DOMRect | null>(null);
//   const theme = useTheme();

//   // Build a semi-transparent version of the theme's paper background so the
//   // overlay always matches whether the app is in light or dark mode.
//   const overlayBg = theme.palette.mode === 'dark'
//     ? 'rgba(26, 29, 36, 0.85)'     // dark paper: #1a1d24 at 85%
//     : 'rgba(255, 255, 255, 0.85)';  // light paper: white at 85%

//   useEffect(() => {
//     const measure = () => {
//       if (wrapperRef.current) {
//         setRect(wrapperRef.current.getBoundingClientRect());
//       }
//     };

//     measure();

//     const el = wrapperRef.current;
//     window.addEventListener('resize', measure);
//     el?.addEventListener('scroll', measure);

//     return () => {
//       window.removeEventListener('resize', measure);
//       el?.removeEventListener('scroll', measure);
//     };
//   }, [wrapperRef]);

//   if (!rect) return null;

//   return (
//     <Box
//       sx={{
//         position: 'fixed',
//         top: rect.top,
//         left: rect.left,
//         width: rect.width,
//         height: rect.height,
//         backgroundColor: overlayBg,
//         backdropFilter: 'blur(2px)',
//         display: 'flex',
//         alignItems: 'center',
//         justifyContent: 'center',
//         zIndex: 1100,
//         pointerEvents: 'all',
//         borderRadius: 'inherit',
//       }}
//     >
//       <Box sx={{ textAlign: 'center' }}>
//         <CircularProgress size={40} />
//         <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
//           Loading returns...
//         </Typography>
//       </Box>
//     </Box>
//   );
// };
// // ─────────────────────────────────────────────────────────────────────────────

// const ReturnsManagement: React.FC = () => {
//   const { currentUser } = useAuth();
//   const [returns, setReturns] = useState<PurchaseOrderReturn[]>([]);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState('');
//   const [deleteLoading, setDeleteLoading] = useState<string>('');

//   const [viewDrawerOpen, setViewDrawerOpen] = useState(false);
//   const [selectedReturn, setSelectedReturn] = useState<PurchaseOrderReturn | null>(null);

//   const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

//   const { companyProfile } = useCompany();

//   // ── Pagination ───────────────────────────────────────────────────────────
//   const [currentPage, setCurrentPage] = useState(1);
//   const [totalReturns, setTotalReturns] = useState(0);
//   const [lastVisible, setLastVisible] = useState<any>(null);
//   const [firstVisible, setFirstVisible] = useState<any>(null);

//   const [totalValue, setTotalValue] = useState(0);
//   const [totalRefunded, setTotalRefunded] = useState(0);

//   // ── Filters ──────────────────────────────────────────────────────────────
//   const [searchInput, setSearchInput] = useState('');
//   const [searchQuery, setSearchQuery] = useState('');
//   const [statusFilter, setStatusFilter] = useState<string>('all');
//   const [showFilters, setShowFilters] = useState(false);

//   // ── Delete confirmation dialog state ──────────────────────────────────────
//   const [deleteTarget, setDeleteTarget] = useState<PurchaseOrderReturn | null>(null);
//   const [deleteDialogLoading, setDeleteDialogLoading] = useState(false);
//   // ──────────────────────────────────────────────────────────────────────────

//   const containerRef = useRef<HTMLDivElement>(null);

//   // Ref for the TableContainer scroll wrapper. Passed to LoadingOverlay so it
//   // can paint a fixed-position backdrop that always covers the full visible
//   // area of the table — even when the user has scrolled down.
//   const tableWrapperRef = useRef<HTMLDivElement>(null);

//   const theme = useTheme();
//   const isDark = theme.palette.mode === 'dark';
//   const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

//   const companyCurrency = companyProfile?.currency || 'LKR';
//   const currencySymbol = getCurrencySymbol(companyCurrency);

//   const showToast = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
//     setToast({ open: true, message, severity });
//   };

//   const handleCloseToast = () => setToast({ ...toast, open: false });

//   // ── Initial load ─────────────────────────────────────────────────────────
//   useEffect(() => {
//     fetchReturns(1, 'first', searchQuery, statusFilter);
//     fetchReturnStats();
//   }, []);

//   // ── Fetch paginated page data ─────────────────────────────────────────────
//   const fetchReturns = async (
//     page: number,
//     direction: 'next' | 'prev' | 'first' = 'first',
//     search: string = '',
//     status: string = 'all',
//     forceNullCursors: boolean = false
//   ) => {
//     try {
//       setLoading(true);
//       setError('');

//       // When forceNullCursors is true (post-save / post-delete / filter reset)
//       // always treat this as a fresh first-page fetch regardless of what
//       // lastVisible / firstVisible currently hold in state.
//       let startAfterDoc = null;
//       let endBeforeDoc = null;

//       if (!forceNullCursors) {
//         if (direction === 'next' && lastVisible) startAfterDoc = lastVisible;
//         else if (direction === 'prev' && firstVisible) endBeforeDoc = firstVisible;
//       }

//       const result = await PurchaseOrderReturnService.getReturnsPaginated(
//         RETURNS_PER_PAGE, startAfterDoc, endBeforeDoc, direction, search,
//         status !== 'all' ? (status as ReturnStatus) : undefined
//       );

//       setReturns(result.returns);
//       setTotalReturns(result.totalCount);
//       setLastVisible(result.lastVisible);
//       setFirstVisible(result.firstVisible);
//       setCurrentPage(page);
//     } catch (err: any) {
//       const errorMessage = err.message || 'Failed to fetch returns';
//       setError(errorMessage);
//       showToast(errorMessage, 'error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   // ── Core helper: reset pagination cursors then fetch page 1 ──────────────
//   //
//   // Call this any time you want to go back to page 1 with fresh cursors
//   // (after delete / status-update / filter change / refresh).
//   // Synchronously clears lastVisible / firstVisible before the async call so
//   // the service always gets null cursors and truly fetches the first page.
//   // ──────────────────────────────────────────────────────────────────────────
//   const resetAndFetchFirstPage = async (
//     search: string = searchQuery,
//     status: string = statusFilter
//   ) => {
//     setCurrentPage(1);
//     setLastVisible(null);
//     setFirstVisible(null);

//     await fetchReturns(
//       1,
//       'first',
//       search,
//       status,
//       /* forceNullCursors */ true
//     );
//   };

//   const fetchReturnStats = async () => {
//     try {
//       const stats = await PurchaseOrderReturnService.getReturnStats();
//       setTotalValue(stats.totalValue);
//       setTotalRefunded(stats.totalRefunded);
//     } catch (err: any) {
//       showToast('Failed to fetch return statistics', 'warning');
//     }
//   };

//   const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
//     if (page > currentPage) fetchReturns(page, 'next', searchQuery, statusFilter);
//     else if (page < currentPage) fetchReturns(page, 'prev', searchQuery, statusFilter);
//     else fetchReturns(page, 'first', searchQuery, statusFilter);
//   };

//   const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
//     if (e.key === 'Enter') {
//       e.preventDefault();
//       setSearchQuery(searchInput);
//       // Reset cursors before fetching so search always starts at page 1
//       resetAndFetchFirstPage(searchInput, statusFilter);
//     }
//   };

//   // ── FIX: delegate entirely to resetAndFetchFirstPage which clears cursors
//   // synchronously before the async fetch, instead of calling fetchReturns
//   // directly with potentially stale cursors in state.
//   const handleStatusFilterChange = (value: string) => {
//     setStatusFilter(value);
//     resetAndFetchFirstPage(searchQuery, value);
//   };

//   const clearFilters = () => {
//     setSearchInput('');
//     setSearchQuery('');
//     setStatusFilter('all');
//     // ── FIX: reset cursors so clearing filters always fetches a true page 1
//     resetAndFetchFirstPage('', 'all');
//     showToast('Filters cleared', 'info');
//   };

//   const hasActiveFilters = searchQuery !== '' || statusFilter !== 'all';
//   const activeFiltersCount = [searchQuery !== '', statusFilter !== 'all'].filter(Boolean).length;

//   // ── Delete ──────────────────────────────────────────────────────────────
//   const handleDeleteReturn = (returnDoc: PurchaseOrderReturn) => {
//     setDeleteTarget(returnDoc);
//   };

//   const handleDeleteConfirm = async () => {
//     if (!deleteTarget) return;
//     try {
//       setDeleteDialogLoading(true);
//       setDeleteLoading(deleteTarget.id!);
//       await PurchaseOrderReturnService.deleteReturn(deleteTarget.id!);
//       setDeleteTarget(null);
//       // ── FIX: was calling fetchReturns(currentPage, ...) with stale cursors.
//       // Reset to page 1 so delete always shows the latest first page.
//       await resetAndFetchFirstPage();
//       await fetchReturnStats();
//       showToast('Return deleted successfully', 'success');
//     } catch (err: any) {
//       showToast(`Failed to delete return: ${err.message || 'Unknown error'}`, 'error');
//     } finally {
//       setDeleteDialogLoading(false);
//       setDeleteLoading('');
//     }
//   };

//   const handleDeleteCancel = () => {
//     setDeleteTarget(null);
//   };
//   // ────────────────────────────────────────────────────────────────────────

//   const handleViewReturn = (returnDoc: PurchaseOrderReturn) => {
//     setSelectedReturn(returnDoc);
//     setViewDrawerOpen(true);
//   };

//   const handleStatusUpdate = async (returnId: string, status: ReturnStatus) => {
//     if (!currentUser) {
//       showToast('You must be logged in to update status', 'error');
//       return;
//     }
//     try {
//       await PurchaseOrderReturnService.updateReturnStatus(
//         returnId, status, currentUser.uid,
//         currentUser.displayName || currentUser.email || 'Unknown'
//       );
//       // ── FIX: reset to page 1 after status update so the change is visible
//       // immediately, especially when a status filter is active.
//       await resetAndFetchFirstPage();
//       await fetchReturnStats();
//       showToast('Return status updated successfully', 'success');
//     } catch (err: any) {
//       showToast(`Failed to update status: ${err.message || 'Unknown error'}`, 'error');
//       throw err;
//     }
//   };

//   const handleRefresh = async () => {
//     // ── FIX: reset to page 1 on refresh so stale cursors don't cause
//     // incorrect page fetches after external changes.
//     await resetAndFetchFirstPage();
//     await fetchReturnStats();
//     showToast('Returns refreshed', 'info');
//   };

//   const getStatusIcon = (status: ReturnStatus) => {
//     switch (status) {
//       case ReturnStatus.APPROVED: return <CheckCircleIcon fontSize="small" />;
//       case ReturnStatus.COMPLETED: return <CheckCircleIcon fontSize="small" />;
//       case ReturnStatus.REJECTED: return <CancelIcon fontSize="small" />;
//       default: return null;
//     }
//   };

//   const totalPages = Math.ceil(totalReturns / RETURNS_PER_PAGE);
//   const totalOutstanding = totalValue - totalRefunded;

//   if (loading && returns.length === 0) {
//     return (
//       <Box display="flex" alignItems="center" justifyContent="center" minHeight="400px">
//         <CircularProgress size={40} />
//         <Typography variant="body1" sx={{ ml: 2 }}>Loading returns...</Typography>
//       </Box>
//     );
//   }

//   return (
//     <Box
//       ref={containerRef}
//       sx={{
//         height: { md: '100%' },
//         display: 'flex',
//         flexDirection: 'column',
//         overflow: { xs: 'visible', md: 'hidden' },
//         p: { xs: 2, sm: 3 },
//       }}
//     >
//       {/* ── Fixed Header ── */}
//       <Box sx={{ flexShrink: 0 }}>
//         <Stack
//           direction={{ xs: 'column', sm: 'row' }}
//           justifyContent="space-between"
//           alignItems={{ xs: 'flex-start', sm: 'center' }}
//           spacing={2}
//           sx={{ mb: 3 }}
//         >
//           <Stack direction="row" alignItems="center" spacing={2}>
//             <AssignmentReturnIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'primary.main' }} />
//             <Box>
//               <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', md: '2rem' } }}>
//                 Purchase Order Returns
//               </Typography>
//               <Typography variant="body2" color="text.secondary">
//                 Manage damaged and returned items
//               </Typography>
//             </Box>
//           </Stack>
//         </Stack>

//         {error && (
//           <Alert
//             severity="error"
//             sx={{ mb: 3 }}
//             onClose={() => setError('')}
//             action={
//               <Button color="inherit" size="small" onClick={handleRefresh} startIcon={<RefreshIcon />}>
//                 Retry
//               </Button>
//             }
//           >
//             {error}
//           </Alert>
//         )}

//         {/* ── Stats Cards ── */}
//         <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }} sx={{ mb: 3 }} alignItems="stretch">

//           {/* Total Returns */}
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
//                   Total Returns
//                 </Typography>
//                 <Typography
//                   variant="h5"
//                   component="div"
//                   color="text.primary"
//                   sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2rem' }, mb: 1 }}
//                 >
//                   {totalReturns}
//                 </Typography>
//                 <Box sx={{ mt: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'success.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="success.main" noWrap>
//                       {returns.filter(r => r.status === ReturnStatus.COMPLETED).length} completed
//                     </Typography>
//                   </Box>
//                   <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
//                     <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'warning.main', flexShrink: 0 }} />
//                     <Typography variant="caption" color="warning.main" noWrap>
//                       {returns.filter(r => r.status === ReturnStatus.PENDING).length} pending
//                     </Typography>
//                   </Box>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Total Value */}
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
//                   Total Value
//                 </Typography>
//                 <Typography
//                   variant="h5"
//                   component="div"
//                   color="text.primary"
//                   sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}
//                 >
//                   {formatCurrency(totalValue, currencySymbol)}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     across all returns
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Total Refunded */}
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
//                   Total Refunded
//                 </Typography>
//                 <Typography
//                   variant="h5"
//                   component="div"
//                   color="text.primary"
//                   sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}
//                 >
//                   {formatCurrency(totalRefunded, currencySymbol)}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalValue > 0 ? `${((totalRefunded / totalValue) * 100).toFixed(1)}% of total` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>

//           {/* Pending Refund */}
//           <Grid item xs={6} sm={6} md={3} sx={{ display: 'flex' }}>
//             <Card sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
//               <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } }, flex: 1, display: 'flex', flexDirection: 'column' }}>
//                 <Typography color="text.secondary" gutterBottom variant="body2" noWrap>
//                   Pending Refund
//                 </Typography>
//                 <Typography
//                   variant="h5"
//                   component="div"
//                   color="text.primary"
//                   sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' }, mb: 1, fontWeight: 700 }}
//                 >
//                   {formatCurrency(totalOutstanding, currencySymbol)}
//                 </Typography>
//                 <Box sx={{ mt: 'auto' }}>
//                   <Typography variant="caption" color="text.secondary" noWrap>
//                     {totalValue > 0 ? `${((totalOutstanding / totalValue) * 100).toFixed(1)}% unrefunded` : '—'}
//                   </Typography>
//                 </Box>
//               </CardContent>
//             </Card>
//           </Grid>
//         </Grid>

//         {/* ── Search & Filters ── */}
//         <Paper sx={{ p: { xs: 1.5, sm: 2 }, mb: 2 }}>
//           <Stack
//             direction={{ xs: 'column', sm: 'row' }}
//             spacing={2}
//             alignItems={{ xs: 'stretch', sm: 'center' }}
//             justifyContent="space-between"
//           >
//             <TextField
//               placeholder="Search by return number, PO... (Press Enter)"
//               value={searchInput}
//               onChange={(e) => setSearchInput(e.target.value)}
//               onKeyPress={handleSearchKeyPress}
//               sx={{ flexGrow: 1, maxWidth: { sm: 400 } }}
//               size="small"
//               InputProps={{
//                 startAdornment: (
//                   <InputAdornment position="start"><SearchIcon /></InputAdornment>
//                 ),
//               }}
//             />
//             <Stack direction="row" alignItems="center" spacing={1}>
//               <Button
//                 onClick={() => setShowFilters(!showFilters)}
//                 variant={hasActiveFilters ? 'contained' : 'outlined'}
//                 startIcon={<FilterListIcon />}
//                 endIcon={showFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
//                 size="small"
//               >
//                 Filters {hasActiveFilters && `(${activeFiltersCount})`}
//               </Button>
//               {hasActiveFilters && (
//                 <Button onClick={clearFilters} variant="text" size="small" startIcon={<ClearIcon />}>
//                   Clear all
//                 </Button>
//               )}
//             </Stack>
//           </Stack>

//           <Collapse in={showFilters}>
//             <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
//               <Grid container spacing={2}>
//                 <Grid item xs={12} sm={6} md={3}>
//                   <FormControl fullWidth size="small">
//                     <InputLabel>Return Status</InputLabel>
//                     <Select
//                       value={statusFilter}
//                       onChange={(e) => handleStatusFilterChange(e.target.value)}
//                       label="Return Status"
//                     >
//                       <MenuItem value="all">All Status</MenuItem>
//                       <MenuItem value={ReturnStatus.PENDING}>Pending</MenuItem>
//                       <MenuItem value={ReturnStatus.APPROVED}>Approved</MenuItem>
//                       <MenuItem value={ReturnStatus.REJECTED}>Rejected</MenuItem>
//                       <MenuItem value={ReturnStatus.COMPLETED}>Completed</MenuItem>
//                       <MenuItem value={ReturnStatus.CANCELLED}>Cancelled</MenuItem>
//                     </Select>
//                   </FormControl>
//                 </Grid>
//                 <Grid item xs={12} md={9}>
//                   <Box sx={{ display: 'flex', alignItems: 'center', height: '40px' }}>
//                     <Typography variant="body2" color="text.secondary">
//                       {hasActiveFilters
//                         ? `Found ${returns.length} returns matching your filters`
//                         : `Showing ${returns.length} returns on this page`}
//                     </Typography>
//                   </Box>
//                 </Grid>
//               </Grid>
//             </Box>
//           </Collapse>
//         </Paper>
//       </Box>
//       {/* ── End Fixed Header ── */}

//       {/* ── Scrollable Body ── */}
//       <Box
//         sx={{
//           flex: 1,
//           overflow: { xs: 'visible', md: 'auto' },
//           display: 'flex',
//           flexDirection: 'column',
//           minHeight: { xs: 'auto', md: 0 },
//         }}
//       >
//         {returns.length === 0 && !loading && (
//           <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
//             <Card sx={{ width: '100%', maxWidth: 500 }}>
//               <CardContent sx={{ textAlign: 'center', py: 6 }}>
//                 <UndoIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
//                 <Typography variant="h5" color="text.secondary" gutterBottom>
//                   {hasActiveFilters ? 'No Returns Match Your Filters' : 'No Returns Found'}
//                 </Typography>
//                 <Typography variant="body1" color="text.secondary" sx={{ mb: 3, maxWidth: 400, mx: 'auto' }}>
//                   {hasActiveFilters
//                     ? 'Try adjusting your search or filter criteria.'
//                     : 'No returns have been created yet.'}
//                 </Typography>
//                 {hasActiveFilters && (
//                   <Button onClick={clearFilters} variant="outlined" startIcon={<ClearIcon />}>
//                     Clear Filters
//                   </Button>
//                 )}
//               </CardContent>
//             </Card>
//           </Box>
//         )}

//         {returns.length > 0 && (
//           <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: { xs: 'auto', md: 0 } }}>

//             {/*
//               ── Scrollable table wrapper ──────────────────────────────────────
//               `ref={tableWrapperRef}` is placed directly on TableContainer.
//               LoadingOverlay calls getBoundingClientRect() on this node and
//               renders a fixed-position backdrop that covers exactly the visible
//               area — regardless of how far the user has scrolled.

//               The overlay is rendered OUTSIDE this TableContainer (just below)
//               so it is never clipped by overflow:auto.

//               The old `position:absolute` overlay that lived *inside* the
//               TableContainer has been removed — it was clipped by overflow:auto
//               and only covered the initially-visible portion when scrolled.
//               ─────────────────────────────────────────────────────────────────
//             */}
//             <TableContainer
//               component={Paper}
//               ref={tableWrapperRef}
//               sx={{
//                 flex: 1,
//                 overflow: { xs: 'auto', md: 'auto' },
//                 maxHeight: { md: '100%' },
//                 mb: 2,
//                 position: 'relative',
//                 '& .MuiTable-root': { minWidth: { xs: 600, md: 1000 } },
//               }}
//             >
//               <Table stickyHeader size={isMobile ? 'small' : 'medium'}>
//                 <TableHead>
//                   <TableRow>
//                     <TableCell>Return Number</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>PO Number</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Supplier</TableCell>
//                     <TableCell>Status</TableCell>
//                     <TableCell align="right">Total Amount</TableCell>
//                     <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Refund</TableCell>
//                     <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>Created By</TableCell>
//                     <TableCell align="right">Actions</TableCell>
//                   </TableRow>
//                 </TableHead>
//                 <TableBody>
//                   {returns.map((returnDoc) => (
//                     <TableRow
//                       key={returnDoc.id}
//                       hover
//                       sx={{
//                         '&:hover': { backgroundColor: 'action.hover' },
//                         '&:last-child td, &:last-child th': { borderBottom: 0 },
//                       }}
//                     >
//                       {/* Return Number */}
//                       <TableCell>
//                         <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
//                           {returnDoc.returnNumber}
//                         </Typography>
//                         <Typography variant="caption" color="text.secondary">
//                           {returnDoc.createdAt.toLocaleDateString()}
//                         </Typography>
//                         {/* PO number on mobile */}
//                         <Typography
//                           variant="caption"
//                           color="text.secondary"
//                           sx={{ display: { xs: 'block', sm: 'none' } }}
//                         >
//                           {returnDoc.purchaseOrderNumber}
//                         </Typography>
//                         {/* Supplier on mobile */}
//                         <Typography
//                           variant="caption"
//                           color="text.secondary"
//                           sx={{ display: { xs: 'block', md: 'none' } }}
//                         >
//                           {returnDoc.supplierName}
//                         </Typography>
//                       </TableCell>

//                       {/* PO Number */}
//                       <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         <Typography variant="body2">{returnDoc.purchaseOrderNumber}</Typography>
//                       </TableCell>

//                       {/* Supplier */}
//                       <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
//                         <Typography variant="body2" sx={{ fontWeight: 500 }}>{returnDoc.supplierName}</Typography>
//                       </TableCell>

//                       {/* Status — isDark passed so chip uses dark-mode palette */}
//                       <TableCell>
//                         <Chip
//                           label={returnDoc.status}
//                           size="small"
//                           variant="outlined"
//                           icon={getStatusIcon(returnDoc.status) || undefined}
//                           sx={getSoftChipSx(
//                             returnStatusToSoftColor[RETURN_STATUS_COLORS[returnDoc.status]] ?? 'default',
//                             isDark
//                           )}
//                         />
//                       </TableCell>

//                       {/* Total Amount */}
//                       <TableCell align="right">
//                         <Typography variant="body2" sx={{ fontWeight: 600, fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
//                           {formatCurrency(returnDoc.totalAmount, currencySymbol)}
//                         </Typography>
//                       </TableCell>

//                       {/* Refund */}
//                       <TableCell align="right" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
//                         {returnDoc.refundIssued ? (
//                           <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>
//                             {formatCurrency(returnDoc.refundAmount, currencySymbol)}
//                           </Typography>
//                         ) : (
//                           <Typography variant="body2" color="text.secondary">-</Typography>
//                         )}
//                       </TableCell>

//                       {/* Created By */}
//                       <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
//                         <Typography variant="body2">{returnDoc.createdByName}</Typography>
//                       </TableCell>

//                       {/* Actions */}
//                       <TableCell align="right">
//                         <Stack direction="row" spacing={0.5} justifyContent="flex-end" flexWrap="nowrap">
//                           <Tooltip title="View Details">
//                             <IconButton onClick={() => handleViewReturn(returnDoc)} size="small" color="primary">
//                               <VisibilityIcon fontSize="small" />
//                             </IconButton>
//                           </Tooltip>
//                           {!returnDoc.inventoryAdjusted && (
//                             <Tooltip title="Delete">
//                               <IconButton
//                                 onClick={() => handleDeleteReturn(returnDoc)}
//                                 disabled={deleteLoading === returnDoc.id}
//                                 size="small"
//                                 color="error"
//                               >
//                                 {deleteLoading === returnDoc.id
//                                   ? <CircularProgress size={16} />
//                                   : <DeleteIcon fontSize="small" />}
//                               </IconButton>
//                             </Tooltip>
//                           )}
//                         </Stack>
//                       </TableCell>
//                     </TableRow>
//                   ))}
//                 </TableBody>
//               </Table>
//             </TableContainer>

//             {/*
//               Rendered OUTSIDE the TableContainer so it is never clipped by
//               overflow:auto. Measures the TableContainer via tableWrapperRef
//               and paints itself at the correct fixed screen coordinates.
//               Dark-mode aware via useTheme() inside LoadingOverlay.
//             */}
//             {loading && <LoadingOverlay wrapperRef={tableWrapperRef} />}

//             {/* Pagination */}
//             <Paper elevation={2} sx={{ flexShrink: 0, borderRadius: 1 }}>
//               <Box sx={{ py: 2, px: 2 }}>
//                 <Stack
//                   direction={{ xs: 'column', sm: 'row' }}
//                   spacing={{ xs: 1.5, sm: 3 }}
//                   alignItems="center"
//                   justifyContent="space-between"
//                   sx={{ width: '100%' }}
//                 >
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Showing {totalReturns === 0 ? 0 : (currentPage - 1) * RETURNS_PER_PAGE + 1}–
//                     {Math.min(currentPage * RETURNS_PER_PAGE, totalReturns)} of {totalReturns} returns
//                   </Typography>
//                   <Pagination
//                     count={Math.max(1, totalPages)}
//                     page={currentPage}
//                     onChange={handlePageChange}
//                     color="primary"
//                     size={isMobile ? 'small' : 'medium'}
//                     showFirstButton
//                     showLastButton
//                     disabled={loading || totalPages <= 1}
//                   />
//                   <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
//                     Page {currentPage} of {Math.max(1, totalPages)}
//                   </Typography>
//                 </Stack>
//               </Box>
//             </Paper>
//           </Box>
//         )}
//       </Box>
//       {/* ── End Scrollable Body ── */}

//       {/* ── Return View Dialog ── */}
//       {selectedReturn && (
//         <ReturnViewDialog
//           open={viewDrawerOpen}
//           onClose={() => { setViewDrawerOpen(false); setSelectedReturn(null); }}
//           return={selectedReturn}
//           onStatusUpdate={handleStatusUpdate}
//           onRefresh={handleRefresh}
//         />
//       )}

//       {/* ── Delete Confirmation Dialog ── */}
//       <Dialog
//         open={!!deleteTarget}
//         onClose={handleDeleteCancel}
//         maxWidth="xs"
//         fullWidth
//       >
//         <DialogTitle>Delete Return</DialogTitle>
//         <DialogContent>
//           <DialogContentText>
//             Are you sure you want to delete{' '}
//             <strong>"{deleteTarget?.returnNumber}"</strong>?{' '}
//             This action cannot be undone.
//           </DialogContentText>
//         </DialogContent>
//         <DialogActions sx={{ px: 3, pb: 2 }}>
//           <Button onClick={handleDeleteCancel} disabled={deleteDialogLoading}>
//             Cancel
//           </Button>
//           <Button
//             variant="contained"
//             color="error"
//             onClick={handleDeleteConfirm}
//             disabled={deleteDialogLoading}
//             startIcon={deleteDialogLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
//           >
//             Delete
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <ToastNotification
//         open={toast.open}
//         message={toast.message}
//         severity={toast.severity}
//         onClose={handleCloseToast}
//       />
//     </Box>
//   );
// };

// export default ReturnsManagement;